import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Zap, CheckCircle2, XCircle, AlertTriangle, Loader2, Send, Trash2, RefreshCw,
  Link2, ShieldCheck, Search, ScrollText, BookKey, Network, Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/integrations/highlevel`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

const SOURCE_LABELS = {
  localbest_admin: { label: "Admin", cls: "bg-[#7C3AED]/15 text-[#A78BFA]" },
  client_dashboard: { label: "Client", cls: "bg-[#3B82F6]/15 text-[#60A5FA]" },
  admin_impersonation: { label: "Admin (impersonating)", cls: "bg-[#F59E0B]/15 text-[#F59E0B]" },
};

const StatusPill = ({ value }) => {
  const map = {
    connected: "bg-[#10B981]/15 text-[#10B981]",
    success: "bg-[#10B981]/15 text-[#10B981]",
    approved: "bg-[#10B981]/15 text-[#10B981]",
    disconnected: "bg-white/10 text-[#94A3B8]",
    expired: "bg-[#F59E0B]/15 text-[#F59E0B]",
    pending: "bg-[#F59E0B]/15 text-[#F59E0B]",
    retrying: "bg-[#F59E0B]/15 text-[#F59E0B]",
    failed: "bg-red-500/15 text-red-400",
    rejected: "bg-red-500/15 text-red-400",
    error: "bg-red-500/15 text-red-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[value] || "bg-white/10 text-[#94A3B8]"}`}>
      {value || "—"}
    </span>
  );
};

const SourceBadge = ({ source }) => {
  const s = SOURCE_LABELS[source] || { label: source || "—", cls: "bg-white/10 text-[#94A3B8]" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

export default function AdminGHL() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("connections");
  const [connections, setConnections] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [logFilter, setLogFilter] = useState("");

  // mapping form state
  const [selConnection, setSelConnection] = useState("");
  const [locations, setLocations] = useState([]);
  const [selLocation, setSelLocation] = useState("");
  const [listingQuery, setListingQuery] = useState("");
  const [listingResults, setListingResults] = useState([]);
  const [selListing, setSelListing] = useState("");
  const [defaultTags, setDefaultTags] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [connRes, mapRes] = await Promise.all([
        axios.get(`${API}/connections`, { headers: authHeader() }),
        axios.get(`${API}/mappings`, { headers: authHeader() }),
      ]);
      setConnections(connRes.data.connections || []);
      setConfigured(connRes.data.configured);
      setMappings(mapRes.data.mappings || []);
      const active = (connRes.data.connections || []).find((c) => c.status === "connected");
      if (active) setSelConnection((prev) => prev || active.id);
    } catch (err) {
      console.error("GHL admin fetch failed:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const ghl = searchParams.get("ghl");
    if (!ghl) return;
    if (ghl === "connected") toast.success("GoHighLevel connected successfully!");
    else toast.error(`GoHighLevel connection failed (${searchParams.get("reason") || "unknown"})`);
    searchParams.delete("ghl"); searchParams.delete("reason"); searchParams.delete("connection_id");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (tab === "logs") fetchSyncLogs();
    if (tab === "audit") fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, logFilter]);

  useEffect(() => {
    if (!selConnection) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/connections/${selConnection}/locations`, { headers: authHeader() });
        setLocations(res.data.locations || []);
        if ((res.data.locations || []).length === 1) setSelLocation(res.data.locations[0].id);
      } catch { setLocations([]); }
    })();
  }, [selConnection]);

  const fetchSyncLogs = async () => {
    try {
      const res = await axios.get(`${API}/sync-logs`, {
        headers: authHeader(),
        params: logFilter ? { status: logFilter, limit: 100 } : { limit: 100 },
      });
      setSyncLogs(res.data.logs || []);
    } catch (err) { console.error(err); }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await axios.get(`${API}/audit-logs`, { headers: authHeader() });
      setAuditLogs(res.data.logs || []);
    } catch (err) { console.error(err); }
  };

  const searchListings = async (q) => {
    setListingQuery(q);
    if (q.length < 2) { setListingResults([]); return; }
    try {
      const res = await axios.get(`${API}/listings`, { headers: authHeader(), params: { q } });
      setListingResults(res.data.listings || []);
    } catch { setListingResults([]); }
  };

  const connect = () => {
    const token = localStorage.getItem("auth_token");
    window.location.href = `${API}/install?token=${encodeURIComponent(token)}&source=admin`;
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(res?.data?.message || okMsg || "Done");
      await fetchAll();
      if (tab === "logs") await fetchSyncLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };

  const createMapping = () => {
    if (!selListing || !selConnection || !selLocation) {
      toast.error("Select a connection, location, and listing");
      return;
    }
    act("map", () => axios.post(`${API}/mappings`, {
      listing_id: selListing,
      connection_id: selConnection,
      ghl_location_id: selLocation,
      default_tags: defaultTags.split(",").map((t) => t.trim()).filter(Boolean),
    }, { headers: authHeader() })).then(() => {
      setSelListing(""); setListingQuery(""); setListingResults([]); setDefaultTags("");
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" /></div>;
  }

  const tabs = [
    { id: "connections", label: "Connections", icon: Network },
    { id: "mappings", label: "Listing Mappings", icon: Link2 },
    { id: "logs", label: "Sync Logs", icon: ScrollText },
    { id: "audit", label: "Audit Logs", icon: BookKey },
  ];

  return (
    <div className="space-y-6" data-testid="admin-ghl-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Zap className="w-7 h-7 text-[#F59E0B]" /> GoHighLevel Integration
          </h1>
          <p className="text-[#94A3B8] mt-1">OAuth marketplace app — connect agencies/locations and map LocalBest listings for lead sync</p>
        </div>
        <Button onClick={connect} className="bg-[#F59E0B] hover:bg-[#FBBF24] text-black font-semibold" data-testid="admin-ghl-connect-btn">
          <Link2 className="w-4 h-4 mr-2" /> Connect GoHighLevel
        </Button>
      </div>

      {!configured && (
        <div className="flex items-center gap-2 text-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg px-4 py-3" data-testid="admin-ghl-not-configured">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          GHL app credentials missing — add GHL_APP_CLIENT_ID and GHL_APP_CLIENT_SECRET to the backend environment, then restart.
        </div>
      )}

      <div className="flex gap-2 border-b border-white/10 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === t.id ? "border-[#F59E0B] text-white" : "border-transparent text-[#64748B] hover:text-white"
            }`} data-testid={`admin-ghl-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* === CONNECTIONS === */}
      {tab === "connections" && (
        <div className="space-y-3">
          {connections.length === 0 && (
            <p className="text-sm text-[#64748B]">No connections yet. Click "Connect GoHighLevel" to install the app on a location or agency.</p>
          )}
          {connections.map((c) => (
            <div key={c.id} className="bg-[#0F172A] border border-white/10 rounded-xl p-5 space-y-3" data-testid={`admin-ghl-conn-${c.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#F59E0B]" />
                  <div>
                    <p className="text-white font-medium">
                      {c.ghl_location_name || c.ghl_company_name || (c.install_type === "agency" ? "Agency Install" : "Location Install")}
                    </p>
                    <p className="text-xs text-[#64748B] font-mono">
                      {c.location_id ? `location: ${c.location_id}` : `company: ${c.company_id}`}
                    </p>
                  </div>
                  <StatusPill value={c.status} />
                  <SourceBadge source={c.connection_source} />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={busy === `rt-${c.id}`}
                    onClick={() => act(`rt-${c.id}`, () => axios.post(`${API}/refresh-token`, { connection_id: c.id }, { headers: authHeader() }))}
                    className="border-white/10 text-white hover:bg-white/10" data-testid={`admin-ghl-refresh-${c.id}`}>
                    {busy === `rt-${c.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Refresh Token
                  </Button>
                  {c.status === "connected" ? (
                    <Button size="sm" variant="ghost" disabled={busy === `disc-${c.id}`}
                      onClick={() => act(`disc-${c.id}`, () => axios.delete(`${API}/connections/${c.id}`, { headers: authHeader() }))}
                      className="text-[#64748B] hover:text-red-400 hover:bg-red-500/10" data-testid={`admin-ghl-disconnect-${c.id}`}>
                      Disconnect
                    </Button>
                  ) : (
                    <Button size="sm" onClick={connect} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid={`admin-ghl-reconnect-${c.id}`}>
                      Reconnect
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-[#94A3B8]">
                <div><span className="text-[#64748B]">Install type:</span> {c.install_type}</div>
                <div><span className="text-[#64748B]">Token expires:</span> {c.token_expires_at ? new Date(c.token_expires_at).toLocaleString() : "—"}</div>
                <div><span className="text-[#64748B]">Connected by:</span> {c.connected_by_admin_id ? "admin" : "client"} {c.impersonation_session_id ? "(impersonation)" : ""}</div>
                <div><span className="text-[#64748B]">Scopes:</span> {(c.scopes || []).length} granted</div>
              </div>
              {(c.scopes || []).length > 0 && (
                <p className="text-[10px] text-[#64748B] font-mono break-all">{(c.scopes || []).join(" · ")}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* === MAPPINGS === */}
      {tab === "mappings" && (
        <div className="space-y-4">
          <div className="bg-[#0F172A] border border-white/10 rounded-xl p-5 space-y-3" data-testid="admin-ghl-mapping-form">
            <p className="text-sm font-semibold text-white">Map a listing to a GHL location</p>
            <div className="flex gap-3 flex-wrap items-start">
              <select value={selConnection} onChange={(e) => setSelConnection(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[180px]" data-testid="admin-ghl-conn-select">
                <option value="" className="bg-[#0F172A]">Connection…</option>
                {connections.filter((c) => c.status === "connected").map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#0F172A]">
                    {c.ghl_location_name || c.ghl_company_name || c.location_id || c.company_id}
                  </option>
                ))}
              </select>
              <select value={selLocation} onChange={(e) => setSelLocation(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[180px]" data-testid="admin-ghl-location-select">
                <option value="" className="bg-[#0F172A]">GHL location…</option>
                {locations.map((l) => <option key={l.id} value={l.id} className="bg-[#0F172A]">{l.name || l.id}</option>)}
              </select>
              <div className="relative min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#64748B]" />
                <Input value={listingQuery} onChange={(e) => searchListings(e.target.value)} placeholder="Search any listing…"
                  className="bg-white/5 border-white/10 text-white pl-9" data-testid="admin-ghl-listing-search" />
                {listingResults.length > 0 && !selListing && (
                  <div className="absolute z-20 mt-1 w-full bg-[#1E293B] border border-white/10 rounded-lg max-h-56 overflow-y-auto shadow-xl">
                    {listingResults.map((l) => (
                      <button key={l.id || l.slug}
                        onClick={() => { setSelListing(l.id || l.slug); setListingQuery(l.name); setListingResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10"
                        data-testid={`admin-ghl-listing-option-${l.slug}`}>
                        {l.name} <span className="text-xs text-[#64748B]">{l.city}{l.state ? `, ${l.state}` : ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Input value={defaultTags} onChange={(e) => setDefaultTags(e.target.value)} placeholder="Default tags (comma separated)"
                className="bg-white/5 border-white/10 text-white max-w-[240px]" data-testid="admin-ghl-default-tags" />
              <Button onClick={createMapping} disabled={busy === "map"} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="admin-ghl-create-mapping-btn">
                {busy === "map" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Create Mapping
              </Button>
            </div>
          </div>

          {mappings.map((m) => (
            <div key={m.id} className="bg-[#0F172A] border border-white/10 rounded-xl p-5 space-y-3" data-testid={`admin-ghl-mapping-${m.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-white font-medium">
                    {m.listing_name} <span className="mx-2 text-[#64748B]">→</span>
                    <span className="text-[#F59E0B]">{m.ghl_location_name || m.ghl_location_id}</span>
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <StatusPill value={m.approval_status} />
                    <SourceBadge source={m.connection_source} />
                    {m.last_sync_at && (
                      <span className="text-xs text-[#64748B]">
                        Last sync: <StatusPill value={m.last_sync_status} /> {new Date(m.last_sync_at).toLocaleString()}
                      </span>
                    )}
                    {(m.default_tags || []).length > 0 && (
                      <span className="text-xs text-[#64748B]">Tags: {(m.default_tags || []).join(", ")}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {m.approval_status === "pending" && (
                    <>
                      <Button size="sm" disabled={busy === `ap-${m.id}`}
                        onClick={() => act(`ap-${m.id}`, () => axios.patch(`${API}/mappings/${m.id}`, { approval_status: "approved" }, { headers: authHeader() }), "Mapping approved")}
                        className="bg-[#10B981] hover:bg-[#34D399] text-black" data-testid={`admin-ghl-approve-${m.id}`}>
                        Approve
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy === `rj-${m.id}`}
                        onClick={() => act(`rj-${m.id}`, () => axios.patch(`${API}/mappings/${m.id}`, { approval_status: "rejected" }, { headers: authHeader() }), "Mapping rejected")}
                        className="text-red-400 hover:bg-red-500/10" data-testid={`admin-ghl-reject-${m.id}`}>
                        Reject
                      </Button>
                    </>
                  )}
                  <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                    Sync
                    <Switch checked={!!m.sync_enabled}
                      onCheckedChange={(v) => act(`tg-${m.id}`, () => axios.patch(`${API}/mappings/${m.id}`, { sync_enabled: v }, { headers: authHeader() }))}
                      data-testid={`admin-ghl-sync-toggle-${m.id}`} />
                  </label>
                  <Button size="sm" variant="outline" disabled={busy === `sf-${m.id}`}
                    onClick={() => act(`sf-${m.id}`, () => axios.post(`${API}/mappings/${m.id}/setup-fields`, {}, { headers: authHeader() }))}
                    className="border-white/10 text-white hover:bg-white/10" data-testid={`admin-ghl-setup-fields-${m.id}`}>
                    {busy === `sf-${m.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1" />} Setup Fields
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === `tl-${m.id}` || m.approval_status !== "approved"}
                    onClick={() => act(`tl-${m.id}`, () => axios.post(`${API}/mappings/${m.id}/test-lead`, {}, { headers: authHeader() }))}
                    className="border-white/10 text-white hover:bg-white/10" data-testid={`admin-ghl-test-lead-${m.id}`}>
                    {busy === `tl-${m.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Test Lead
                  </Button>
                  <button disabled={busy === `dm-${m.id}`}
                    onClick={() => act(`dm-${m.id}`, () => axios.delete(`${API}/mappings/${m.id}`, { headers: authHeader() }))}
                    className="p-1.5 rounded hover:bg-red-500/10 text-[#64748B] hover:text-red-400" data-testid={`admin-ghl-delete-mapping-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {mappings.length === 0 && <p className="text-sm text-[#64748B]">No listing mappings yet.</p>}
        </div>
      )}

      {/* === SYNC LOGS === */}
      {tab === "logs" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            {["", "success", "failed", "retrying"].map((f) => (
              <button key={f || "all"} onClick={() => setLogFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${logFilter === f ? "bg-[#F59E0B] text-black" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                data-testid={`admin-ghl-logfilter-${f || "all"}`}>
                {f || "All"}
              </button>
            ))}
          </div>
          {syncLogs.map((log) => (
            <div key={log.id} className="bg-[#0F172A] border border-white/10 rounded-lg p-4" data-testid={`admin-ghl-synclog-${log.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  {log.sync_status === "success" ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-red-400" />}
                  <span className="text-white font-medium">{log.action}</span>
                  {log.is_test && <span className="px-1.5 py-0.5 rounded bg-[#3B82F6]/15 text-[#60A5FA] text-[10px]">TEST</span>}
                  <StatusPill value={log.sync_status} />
                  <span className="text-[#94A3B8] text-xs">{log.listing_name || log.listing_slug || "—"} → {log.ghl_location_id}</span>
                  <span className="text-[#64748B] text-xs">{new Date(log.created_at).toLocaleString()}</span>
                  {log.retry_count > 0 && <span className="text-[#64748B] text-xs">retries: {log.retry_count}/3</span>}
                </div>
                {(log.sync_status === "failed" || log.sync_status === "retrying") && (
                  <Button size="sm" variant="ghost" disabled={busy === `rl-${log.id}`}
                    onClick={() => act(`rl-${log.id}`, () => axios.post(`${API}/sync-logs/${log.id}/retry`, {}, { headers: authHeader() }))}
                    className="text-[#F59E0B] hover:bg-[#F59E0B]/10" data-testid={`admin-ghl-retrylog-${log.id}`}>
                    {busy === `rl-${log.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Retry Now"}
                  </Button>
                )}
              </div>
              {log.response_body_redacted && (
                <pre className="mt-2 px-3 py-2 bg-black/30 rounded text-[10px] text-[#94A3B8] font-mono overflow-x-auto max-h-24">
                  {typeof log.response_body_redacted === "string" ? log.response_body_redacted : JSON.stringify(log.response_body_redacted)}
                </pre>
              )}
            </div>
          ))}
          {syncLogs.length === 0 && <p className="text-sm text-[#64748B]">No sync activity yet.</p>}
        </div>
      )}

      {/* === AUDIT LOGS === */}
      {tab === "audit" && (
        <div className="space-y-2">
          {auditLogs.map((log) => (
            <div key={log.id} className="bg-[#0F172A] border border-white/10 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-ghl-audit-${log.id}`}>
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className="text-white font-medium">{log.action}</span>
                {log.impersonation_session_id && <SourceBadge source="admin_impersonation" />}
                {log.metadata?.source && !log.impersonation_session_id && <SourceBadge source={log.metadata.source} />}
                <span className="text-xs text-[#64748B]">
                  {log.user_id ? `user: ${String(log.user_id).slice(0, 8)}` : ""}
                  {log.admin_id ? ` admin: ${String(log.admin_id).slice(0, 8)}` : ""}
                </span>
              </div>
              <span className="text-xs text-[#64748B]">{new Date(log.created_at).toLocaleString()}</span>
            </div>
          ))}
          {auditLogs.length === 0 && <p className="text-sm text-[#64748B]">No audit entries yet.</p>}
        </div>
      )}
    </div>
  );
}
